import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Eye, 
  Loader2,
  ArrowUp,
  ExternalLink
} from 'lucide-react';
import api from '../../lib/api';
import Chip from '../Chip';
import RecordCount from '../RecordCount';
import SearchField from '../SearchField';
import {
  StandardTable,
  StandardTableHeader,
  StandardTableHeaderCell,
  StandardTableBody,
  StandardTableRow,
  StandardTableCell,
} from '../StandardTable';
import StatCard from '../StatCard';
import { LOBBYING_API_TIMEOUT_MS } from './constants';
import { createLobbyingBillFilterUrl } from '../../utils/billStatusMapping';
import ViewToggle from '../ViewToggle';
import FilterActions from '../FilterActions';

interface LobbyistSummary {
  id: string;
  primary_lobbyist_id: string;
  lobbyist_name: string;
  lobbyist_firm_name?: string;
  lobbyist_city?: string;
  lobbyist_state?: string;
  status: string;
  client_count: number;
  bill_count: number;
}

interface LobbyistsSummaryResponse {
  lobbyists: LobbyistSummary[];
  total: number;
  returned_count: number;
  has_more: boolean;
  offset: number;
  limit: number;
}

interface AlphabetIndexItem {
  letter: string;
  lobbyist_count?: number;
  total_count?: number;
}

type SortField = 'name' | 'firm' | 'client_count' | 'bill_count';
type SortDirection = 'asc' | 'desc';

const LobbyistsTab: React.FC = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<'table' | 'card'>('table');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMode, setSearchMode] = useState<'starts_with' | 'contains'>('starts_with');
  const [lobbyists, setLobbyists] = useState<LobbyistSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMoreLobbyists, setHasMoreLobbyists] = useState(false);
  const [alphabetIndex, setAlphabetIndex] = useState<AlphabetIndexItem[]>([]);
  const [statusFilter, setStatusFilter] = useState('current'); // Default to current lobbyists
  
  // Scroll to top state
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  
  // Background loading timer ref to cancel when filters change
  const backgroundLoadingTimerRef = useRef<number | null>(null);
  const fetchRequestIdRef = useRef(0);
  const listLengthRef = useRef(0);
  
  // Ref to track current filter state for background loading decisions
  const currentFiltersRef = useRef({ searchQuery: '', statusFilter: 'current' });
  
  listLengthRef.current = lobbyists.length;
  
  // Sorting state
  const [sortBy, setSortBy] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Fetch lobbyists with aggregated counts (progressive loading)
  const fetchLobbyists = useCallback(async (isInitialLoad = true) => {
    const requestId = ++fetchRequestIdRef.current;
    try {
      // Cancel any existing background loading timer when starting a new request
      if (isInitialLoad && backgroundLoadingTimerRef.current) {
        clearTimeout(backgroundLoadingTimerRef.current);
        backgroundLoadingTimerRef.current = null;
      }
      
      // Update current filter state ref
      if (isInitialLoad) {
        currentFiltersRef.current = { searchQuery, statusFilter };
        setLoading(true);
        setLobbyists([]);
        setTotalCount(0);
      } else {
        setLoadingMore(true);
      }

      const params = new URLSearchParams({
        sortBy: sortBy,
        sortDirection: sortDirection,
        search_mode: searchMode,
        ...(searchQuery && { search: searchQuery }),
        ...(statusFilter && statusFilter !== 'all' && { status: statusFilter }),
        limit: isInitialLoad ? '100' : '1000',
        offset: isInitialLoad ? '0' : listLengthRef.current.toString(),
        get_total: 'true'
      });


      const response = await api.get(`/lobbying/lobbyists-summary?${params}`, {
        timeout: LOBBYING_API_TIMEOUT_MS,
      });

      if (requestId !== fetchRequestIdRef.current) {
        return;
      }
      
      if (response.data.success) {
        const data: LobbyistsSummaryResponse = response.data.data;
        
        if (isInitialLoad) {
          setLobbyists(data.lobbyists);
          setTotalCount(data.total || data.lobbyists.length);
          setHasMoreLobbyists(data.has_more || false);
          // DISABLED: Background loading completely disabled to prevent count issues
          // Users can use "Load More" button when needed
        } else {
          // Appending additional lobbyists to existing list
          setLobbyists(prev => [...prev, ...data.lobbyists]);
          setTotalCount(data.total ?? listLengthRef.current + data.lobbyists.length);
          setHasMoreLobbyists(data.has_more || false);
        }
      }
    } catch (error) {
      if (requestId !== fetchRequestIdRef.current) {
        return;
      }
      console.error('Error fetching lobbyists summary:', error);
      if (isInitialLoad) {
        setLobbyists([]);
      }
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        if (isInitialLoad) {
          setLoading(false);
        } else {
          setLoadingMore(false);
        }
      }
    }
  }, [searchQuery, searchMode, statusFilter, sortBy, sortDirection]);

  // Fetch alphabet index
  const fetchAlphabetIndex = async () => {
    try {
      const response = await api.get('/lobbying/lobbyists-alphabet-index');
      
      if (response.data.success) {
        // Group numeric letters into "0-9"
        const processedData = [];
        let numericCount = 0;
        
        for (const item of response.data.data) {
          if (/^\d/.test(item.letter)) {
            numericCount += item.lobbyist_count || 0;
          } else {
            processedData.push(item);
          }
        }
        
        // Add "0-9" group if there are numeric entries
        if (numericCount > 0) {
          processedData.unshift({
            letter: '0-9',
            lobbyist_count: numericCount,
            total_count: numericCount
          });
        }
        
        // Sort: 0-9 first, then alphabetically
        processedData.sort((a, b) => {
          if (a.letter === '0-9') return -1;
          if (b.letter === '0-9') return 1;
          return a.letter.localeCompare(b.letter);
        });
        
        setAlphabetIndex(processedData);
      }
    } catch (error) {
      console.error('Error fetching alphabet index:', error);
      setAlphabetIndex([]);
    }
  };

  // Handle sorting
  const handleSort = (field: string) => {
    const sortField = field as SortField;
    if (sortBy === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(sortField);
      setSortDirection('asc');
    }
  };

  // Navigate to filtered legislation page
  const handleBillCountClick = (lobbyistId: string, lobbyistName: string) => {
    navigate(
      createLobbyingBillFilterUrl({
        lobbyistId,
        lobbyistName,
        ...(statusFilter && statusFilter !== 'all' && { lobbyistStatus: statusFilter }),
      })
    );
  };

  const handleClientCountClick = (lobbyistId: string, lobbyistName: string) => {
    const params = new URLSearchParams({
      lobbyist_id: lobbyistId,
      lobbyist_name: lobbyistName,
    });
    navigate(`/lobbying?${params.toString()}#clients`);
  };

  // Handle alphabet filter
  const handleAlphabetFilter = (letter: string) => {
    setSearchQuery(letter);
    setSearchMode('starts_with');
  };

  // Navigate to lobbyist detail page
  const handleLobbyistClick = (lobbyistId: string) => {
    navigate(`/lobbyists/${lobbyistId}`);
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchQuery('');
    setSearchMode('starts_with');
    setStatusFilter('current');
  };

  // Load more lobbyists function
  const loadMoreLobbyists = useCallback(() => {
    if (!loadingMore && hasMoreLobbyists) {
      fetchLobbyists(false);
    }
  }, [loadingMore, hasMoreLobbyists, fetchLobbyists]);

  // Scroll to top functionality
  const scrollToTop = () => {
    // Scroll both window and main container to top
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    
    // Also scroll the main container if it exists and is scrolled
    const mainContainers = document.querySelectorAll('main.overflow-y-auto');
    for (const container of mainContainers) {
      if (container.scrollHeight > container.clientHeight && container.clientHeight > 0 && container.scrollTop > 0) {
        container.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    }
  };

  useEffect(() => {
    fetchAlphabetIndex();
  }, []);

  // Single fetch path — debounce typing only (avoid duplicate mount + debounced requests)
  useEffect(() => {
    const delay = searchQuery.trim() ? 300 : 0;
    const timeoutId = window.setTimeout(() => {
      fetchLobbyists(true);
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchMode, statusFilter, sortBy, sortDirection, fetchLobbyists]);

  // Handle scroll event to show/hide scroll to top button and infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      // Check both window scroll and main container scroll
      const windowScrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      // Find the main scrollable container (the one with actual content)
      const mainContainers = document.querySelectorAll('main.overflow-y-auto');
      let containerScrollTop = 0;
      let mainContainer = null;
      
      // Find the main container that actually has scrollable content
      for (const container of mainContainers) {
        if (container.scrollHeight > container.clientHeight && container.clientHeight > 0) {
          containerScrollTop = Math.max(containerScrollTop, container.scrollTop);
          mainContainer = container;
        }
      }
      
      const totalScroll = Math.max(windowScrollTop, containerScrollTop);
      setShowScrollToTop(totalScroll > 300); // Show button after scrolling 300px

      // Infinite scroll detection
      if (hasMoreLobbyists && !loadingMore) {
        let scrollThreshold;
        
        if (mainContainer) {
          // Check if we're near the bottom of the main container
          const containerBottom = mainContainer.scrollHeight - mainContainer.scrollTop - mainContainer.clientHeight;
          scrollThreshold = containerBottom < 200; // Trigger when 200px from bottom
        } else {
          // Fallback to window scroll detection
          const windowBottom = document.documentElement.scrollHeight - window.innerHeight - windowScrollTop;
          scrollThreshold = windowBottom < 200;
        }
        
        if (scrollThreshold) {
          loadMoreLobbyists();
        }
      }
    };

    const setupScrollListeners = () => {
      // Add listeners to both window and main container
      window.addEventListener('scroll', handleScroll);
      
      // Find the main container that actually has scrollable content
      const mainContainers = document.querySelectorAll('main.overflow-y-auto');
      for (const container of mainContainers) {
        if (container.scrollHeight > container.clientHeight && container.clientHeight > 0) {
          container.addEventListener('scroll', handleScroll);
          return container;
        }
      }
      return null;
    };

    // Try to setup listeners immediately
    let mainContainer = setupScrollListeners();

    // If main container wasn't ready, try again after lobbyists load
    let retryTimer: number | null = null;
    if (!mainContainer && lobbyists.length === 0) {
      retryTimer = window.setTimeout(() => {
        mainContainer = setupScrollListeners();
      }, 1000);
    }

    // Force show button if page is tall and has many lobbyists
    if (lobbyists.length >= 50) {
      const checkPageHeight = () => {
        const bodyHeight = document.body.scrollHeight;
        const windowHeight = window.innerHeight;
        if (bodyHeight > windowHeight * 2) {
          handleScroll();
        }
      };
      setTimeout(checkPageHeight, 100);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (mainContainer) {
        mainContainer.removeEventListener('scroll', handleScroll);
      }
      if (retryTimer !== null) {
        clearTimeout(retryTimer);
      }
    };
  }, [lobbyists, hasMoreLobbyists, loadingMore, loadMoreLobbyists]); // Re-run when lobbyists change or infinite scroll state changes

  const getStatusVariant = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'current':
        return 'success';
      case 'inactive':
        return 'neutral';
      case 'suspended':
        return 'danger';
      default:
        return 'neutral';
    }
  };

  const renderCard = (lobbyist: LobbyistSummary) => (
    <div
      key={`lobbyist-${lobbyist.id}-${lobbyist.lobbyist_name}`}
      className="card hover:shadow-lg transition-shadow duration-200 cursor-pointer"
      onClick={() => handleLobbyistClick(lobbyist.id)}
    >
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary mb-1">
            {lobbyist.lobbyist_name}
          </h3>
          {lobbyist.lobbyist_firm_name && (
            <p className="text-sm text-text-secondary dark:text-text-dark-secondary mb-2">
              {lobbyist.lobbyist_firm_name}
            </p>
          )}
        </div>
        <Chip 
          label={lobbyist.status || 'Unknown'}
          variant={getStatusVariant(lobbyist.status)}
          size="sm"
        />
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <StatCard
          label="Clients"
          value={lobbyist.client_count}
          onClick={() => handleClientCountClick(lobbyist.id, lobbyist.lobbyist_name)}
        />
        <StatCard
          label="Bills"
          value={lobbyist.bill_count}
          onClick={() => handleBillCountClick(lobbyist.id, lobbyist.lobbyist_name)}
        />
      </div>

      {lobbyist.lobbyist_city && lobbyist.lobbyist_state && (
        <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
          {lobbyist.lobbyist_city}, {lobbyist.lobbyist_state}
        </p>
      )}

      <div className="mt-4 pt-4 border-t border-border-light dark:border-border-dark">
        <button className="w-full flex items-center justify-center gap-2 text-sm text-text-tertiary dark:text-text-dark-tertiary hover:text-text-secondary dark:hover:text-text-dark-secondary font-medium">
          <Eye className="w-4 h-4" />
          View Details
        </button>
      </div>
    </div>
  );

  const renderTable = () => (
    <StandardTable>
      <StandardTableHeader>
        <StandardTableRow>
          <StandardTableHeaderCell
            sortable
            sortKey="name"
            currentSortKey={sortBy}
            sortDirection={sortDirection}
            onSort={handleSort}
            columnType="auto"
          >
            Lobbyist
          </StandardTableHeaderCell>
          <StandardTableHeaderCell
            sortable
            sortKey="firm"
            currentSortKey={sortBy}
            sortDirection={sortDirection}
            onSort={handleSort}
            columnType="auto"
            responsiveHide="md"
          >
            Firm
          </StandardTableHeaderCell>
          <StandardTableHeaderCell
            sortable
            sortKey="client_count"
            currentSortKey={sortBy}
            sortDirection={sortDirection}
            onSort={handleSort}
            columnType="fit"
          >
            Clients
          </StandardTableHeaderCell>
          <StandardTableHeaderCell
            sortable
            sortKey="bill_count"
            currentSortKey={sortBy}
            sortDirection={sortDirection}
            onSort={handleSort}
            columnType="fit"
          >
            Bills
          </StandardTableHeaderCell>
          <StandardTableHeaderCell
            columnType="actions"
            align="right"
          >
            Actions
          </StandardTableHeaderCell>
        </StandardTableRow>
      </StandardTableHeader>
      <StandardTableBody>
        {lobbyists.map((lobbyist) => (
          <StandardTableRow 
            key={`lobbyist-${lobbyist.id}-${lobbyist.lobbyist_name}`}
            onClick={() => handleLobbyistClick(lobbyist.id)}
          >
            <StandardTableCell>
              <div className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                {lobbyist.lobbyist_name}
              </div>
            </StandardTableCell>
            <StandardTableCell responsiveHide="md">
              {lobbyist.lobbyist_firm_name || '-'}
            </StandardTableCell>
            <StandardTableCell>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleClientCountClick(lobbyist.id, lobbyist.lobbyist_name);
                }}
                className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium flex items-center gap-1"
              >
                {lobbyist.client_count}
                <ExternalLink className="w-3 h-3" />
              </button>
            </StandardTableCell>
            <StandardTableCell>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleBillCountClick(lobbyist.id, lobbyist.lobbyist_name);
                }}
                className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium flex items-center gap-1"
              >
                {lobbyist.bill_count}
                <ExternalLink className="w-3 h-3" />
              </button>
            </StandardTableCell>
            <StandardTableCell align="right">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleLobbyistClick(lobbyist.id);
                }}
                className="p-1 text-text-tertiary dark:text-text-dark-tertiary hover:text-text-primary dark:hover:text-text-dark-primary transition-colors"
                title="View details"
                aria-label="View details"
              >
                <Eye className="w-4 h-4" />
              </button>
            </StandardTableCell>
          </StandardTableRow>
        ))}
      </StandardTableBody>
    </StandardTable>
  );

  const showInitialLoader = loading && lobbyists.length === 0;

  return (
    <div className="space-y-6">
      {/* Alphabet Filter */}
      <div className="flex flex-wrap gap-1 p-4 bg-background-tertiary dark:bg-background-dark-tertiary rounded-lg">
        {/* All button */}
        <button
          onClick={() => {
            setSearchQuery('');
            setSearchMode('starts_with');
          }}
          className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
            !searchQuery
              ? 'bg-primary-500 text-white'
              : 'bg-background dark:bg-background-dark-secondary text-text-secondary dark:text-text-dark-secondary hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary'
          }`}
        >
          All
        </button>
        
        {/* Numbers button */}
        {(() => {
          const numbersData = alphabetIndex.find(item => item.letter === '0-9');
          const isActive = searchQuery === '0-9' && searchMode === 'starts_with';
          const hasData = numbersData && (numbersData.lobbyist_count ?? 0) > 0;
          
          return (
            <button
              onClick={() => handleAlphabetFilter('0-9')}
              disabled={!hasData}
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                isActive
                  ? 'bg-primary-500 text-white'
                  : hasData
                  ? 'bg-background dark:bg-background-dark-secondary text-text-secondary dark:text-text-dark-secondary hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary'
                  : 'bg-background-tertiary dark:bg-background-dark-tertiary text-text-tertiary dark:text-text-dark-tertiary cursor-not-allowed'
              }`}
              title={hasData ? `${numbersData?.lobbyist_count || 0} lobbyists` : 'No lobbyists'}
            >
              0-9
            </button>
          );
        })()}
        
        {Array.from('ABCDEFGHIJKLMNOPQRSTUVWXYZ').map(letter => {
          const letterData = alphabetIndex.find(item => item.letter === letter);
          const isActive = searchQuery === letter && searchMode === 'starts_with';
          const hasData = letterData && (letterData.lobbyist_count ?? 0) > 0;
          
          return (
            <button
              key={letter}
              onClick={() => handleAlphabetFilter(letter)}
              disabled={!hasData}
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                isActive
                  ? 'bg-primary-500 text-white'
                  : hasData
                  ? 'bg-background dark:bg-background-dark-secondary text-text-secondary dark:text-text-dark-secondary hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary'
                  : 'bg-background-tertiary dark:bg-background-dark-tertiary text-text-tertiary dark:text-text-dark-tertiary cursor-not-allowed'
              }`}
              title={hasData ? `${letterData?.lobbyist_count || 0} lobbyists` : 'No lobbyists'}
            >
              {letter}
            </button>
          );
        })}
      </div>

      {/* Status Filter */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
            Status:
          </label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input w-auto min-w-[140px]"
          >
            <option value="current">Current Only</option>
            <option value="all">All Statuses</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
            <option value="terminated">Terminated</option>
            <option value="expired">Expired</option>
          </select>
        </div>
      </div>

      {/* Search Bar and Clear Filters */}
      <div className="mb-4 flex items-center gap-4">
        <SearchField
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search lobbyists by name or organization..."
          className="max-w-md flex-1"
        />
        <FilterActions
          onClear={handleClearFilters}
          hasFilters={Boolean(searchQuery) || statusFilter !== 'current'}
        />
      </div>

      {/* View Toggle and Results */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <ViewToggle value={viewMode} onChange={setViewMode} />
        </div>
        <RecordCount
          label="lobbyists"
          showing={lobbyists.length}
          filtered={totalCount}
          total={totalCount}
          isLoading={loading}
        />
      </div>

      {/* Results */}
      {showInitialLoader ? (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      ) : (
        <>
          {viewMode === 'card' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {lobbyists.map(renderCard)}
            </div>
          ) : (
            renderTable()
          )}
          
          {/* Load More button when there are more lobbyists */}
          {hasMoreLobbyists && (
            <div className="flex justify-center mt-6">
              <button
                onClick={() => fetchLobbyists(false)}
                disabled={loadingMore}
                className="px-6 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
              >
                {loadingMore ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading more...
                  </>
                ) : (
                  `Load More Lobbyists (${totalCount - lobbyists.length} remaining)`
                )}
              </button>
            </div>
          )}
        </>
      )}

      {/* Scroll to top button */}
      {showScrollToTop && (
        <>
          {/* Mobile: Above bottom navigation */}
          <button
            onClick={scrollToTop}
            className="lg:hidden fixed bottom-20 left-1/2 transform -translate-x-1/2 z-50 p-3 bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-lg transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            title="Go to top"
          >
            <ArrowUp className="w-4 h-4" />
          </button>
          
          {/* Desktop: Bottom right corner */}
          <button
            onClick={scrollToTop}
            className="hidden lg:block fixed bottom-6 right-6 z-50 p-3 bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-lg transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            title="Go to top"
          >
            <ArrowUp className="w-5 h-5" />
          </button>
        </>
      )}
    </div>
  );
};

export default LobbyistsTab;
